import _ from "lodash";
import React from "react";
import classes from "./ReorderImages.scss";
import UIButton from "UIComponents/UIButton";
import DropzoneComponent from "UIComponents/DropzoneComponent";
import NoDataComponent from "UIComponents/NoDataComponent";
import I18nHOC from "UIComponents/I18nHOC";
import { connect } from "react-redux";
import { CancelIcon, AddIcon, MoveIcon } from "SvgComponents";
import { colors } from "Constants";
import { getRethumbUrl } from "Utils";
import arrayMove from "array-move";
import ScreenPanel from "../../ScreenPanel";
import { compose } from "lib/apolloClient";
import {
  setToastMsg,
  enableGlobalHeader,
  disableGlobalHeader,
} from "Login/modules/LoginModule";

import {
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import { SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS as cssDndKit } from "@dnd-kit/utilities";
import { isGlobalHeaderOnMultipleScreensEnabled } from "Post/utils";
import { getSettingValue } from "modules/PermissionModule";

const isLocalUrl = url => {
  return _.includes(url, "blob");
};

const getRethumb = imageUrl => {
  const rethumpUrl = isLocalUrl(imageUrl)
    ? imageUrl
    : imageUrl
    ? getRethumbUrl({
        width: 256,
        height: 256,
        imageUrl: imageUrl,
        fitIn: true,
      })
    : "";
  return `url(${rethumpUrl})`;
};

const SortableItem = ({ item, id, onCancelItemClick }) => {
  const { setNodeRef, transform, transition, listeners } = useSortable({ id });

  const style = {
    transform: cssDndKit.Transform.toString(transform),
    transition,
    backgroundImage: getRethumb(item.url),
  };

  return (
    <div
      ref={setNodeRef}
      className={classes.itemContainer}
      {...listeners}
      style={style}
    >
      <div className={classes.overlayContainer}>
        <div className={classes.dragIconContainer}>
          <MoveIcon fill={colors.neutral200} width={16} height={16} />
        </div>
      </div>
      <div
        className={classes.itemCloseIcon}
        onClick={() => onCancelItemClick(item)}
      >
        <CancelIcon width={7} height={7} />
      </div>
    </div>
  );
};

const SortableList = ({ items, onSortEnd, onCancelItemClick }) => {
  const sensors = useSensors(
    useSensor(MouseSensor, {
      // Require the mouse to move by 10 pixels before activating.
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: 10,
      },
    })
  );
  return (
    <DndContext
      sensors={sensors}
      autoScroll={false}
      onDragEnd={({ active, over }) => {
        if (over && active.id !== over?.id) {
          const activeIndex = items.findIndex(({ id }) => id === active.id);
          const overIndex = items.findIndex(({ id }) => id === over.id);
          onSortEnd(activeIndex, overIndex);
        }
      }}
      collisionDetection={closestCenter}
    >
      <SortableContext items={items}>
        <div className={classes.imagesContainer}>
          {items.map(item => (
            <SortableItem
              key={`item-${item.id}`}
              item={item}
              id={item.id}
              onCancelItemClick={onCancelItemClick}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

class ReoderImagesModal extends React.PureComponent {
  constructor(props) {
    super(props);
    const {
      postDetails: { attachments = [] },
    } = props.screenPanelProps;
    this.state = { attachments };
  }

  componentDidMount() {
    const { disableGlobalHeader } = this.props;
    disableGlobalHeader();
  }

  componentWillUnmount() {
    const { enableGlobalHeader, screenPanelProps } = this.props;
    const { parentType } = screenPanelProps;
    isGlobalHeaderOnMultipleScreensEnabled() &&
      (parentType === "POST" || _.isUndefined(parentType)) &&
      enableGlobalHeader();
  }

  onSortEnd = (oldIndex, newIndex) => {
    this.setState({
      attachments: arrayMove(this.state.attachments, oldIndex, newIndex),
    });
  };
  updateAttachments = attachment => {
    this.setState({ attachments: [...this.state.attachments, attachment] });
  };

  onDoneClick = () => {
    const {
      screenPanelProps: { updatePost, goToStep },
      setToastMsg,
      maxAttachmentCount,
    } = this.props;
    const { attachments } = this.state;
    if (attachments.length > maxAttachmentCount) {
      setToastMsg({
        msg: "journal:can_select_max_allowed_size_images",
        locale_params: [
          {
            key: "size",
            value: maxAttachmentCount,
            isPlainText: true,
          },
        ],
      });
    } else {
      goToStep("POST_DETAIL");
      updatePost({ attachments: attachments, postType: "PHOTO" });
    }
  };

  onCancelItemClick = item => {
    this.setState({
      attachments: _.filter(
        this.state.attachments,
        attachment => attachment.url != item.url
      ),
    });
  };

  getFooter = () => {
    const { attachments } = this.state;
    const { t } = this.props;
    return (
      <div className={classes.footer}>
        <div className={classes.dropzoneContainer}>
          <DropzoneComponent
            multiple={true}
            parentId={null}
            parentType={"POST"}
            renderComponent={
              <div className={classes.addMoreContainer}>
                <div className={classes.addIconContainer}>
                  <AddIcon width={6} height={6} fill={colors.blue400} />
                </div>
                <div className={classes.addMoreText}>
                  {t("journal:add_more_images")}
                </div>
              </div>
            }
            onDropContent={this.updateAttachments}
            shouldUpload={false}
            accept={[
              "image/jpeg",
              "image/png",
              "image/jpg",
              "image/heic",
              "image/heif",
            ]}
          />
        </div>
        <UIButton
          size="sm"
          color="pink"
          onClick={this.onDoneClick}
          isDisabled={_.get(attachments, "length", 0) == 0}
        >
          {t("common:done")}
        </UIButton>
      </div>
    );
  };

  render() {
    const { attachments } = this.state;
    const { t, screenPanelProps = {} } = this.props;
    return (
      <ScreenPanel {...screenPanelProps} footerComponent={this.getFooter()}>
        <div className={classes.container}>
          <div className={classes.headerContainer}>
            <div className={classes.heading}>{t("journal:reorder_images")}</div>
            <div className={classes.headerSubText}>
              {t("journal:hold_drag_images")}
            </div>
          </div>

          {_.get(attachments, "length", 0) > 0 ? (
            <SortableList
              items={attachments}
              onSortEnd={this.onSortEnd}
              onCancelItemClick={this.onCancelItemClick}
            />
          ) : (
            <NoDataComponent
              emptyText={t("common:no_with_label", {
                label: t("common:image_plural"),
              })}
              isSearch={false}
              emptyHeaderText={""}
            />
          )}
        </div>
      </ScreenPanel>
    );
  }
}

const mapActionCreators = {
  setToastMsg,
  enableGlobalHeader,
  disableGlobalHeader,
};
const mapStateToProps = (state, ownProps) => {
  const maxAttachmentCount = getSettingValue({
    name: "PortfolioAttachmentCount",
    organizationId: state.login.userInfo.org_id,
  });

  return {
    maxAttachmentCount,
  };
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  I18nHOC({ resource: ["common", "journal"] })
)(ReoderImagesModal);
